import type { ColorMode } from "./agents";

/**
 * Speaker/diarization color assignment for transcript surfaces — the same
 * identity problem as agents.ts ("who is talking"), so the same shape:
 * chips render soft + text (AA by construction), timeline marks and border
 * accents render primary, and every slot has a light + dark variant.
 *
 * Hue order (indigo, orange, teal, purple, green, blue) and per-mode steps
 * were chosen with the dataviz palette validator (2026-08-19): both mode
 * palettes pass all six checks against their real surfaces (light on #fff,
 * dark on navy900 #1a1c29) — lightness band, chroma floor, adjacent-pair
 * CVD separation, normal-vision floor, and ≥3:1 mark contrast. Every text
 * step is ≥6.5:1 on its surface AND on its own soft tint. Keep the order:
 * adjacent pairs were validated as neighbors — re-sorting can reintroduce
 * CVD collisions.
 */
export interface SpeakerColor {
  /** Marks: timeline bars, border accents, color dots. Not for text. */
  primary: string;
  /** Chip/tag background. */
  soft: string;
  /** Chip/tag border. */
  border: string;
  /** Labels: chip/tag text (AA against `soft` over this mode's surface)
   *  and speaker names on the dark video scrim. */
  text: string;
}

const SPEAKER_PALETTE_LIGHT: SpeakerColor[] = [
  { primary: "#5C6BC0", soft: "rgba(92, 107, 192, 0.08)", border: "rgba(92, 107, 192, 0.25)", text: "#283593" }, // indigo
  { primary: "#C2410C", soft: "rgba(194, 65, 12, 0.08)", border: "rgba(194, 65, 12, 0.25)", text: "#9A3412" }, // orange
  { primary: "#0D9488", soft: "rgba(13, 148, 136, 0.08)", border: "rgba(13, 148, 136, 0.25)", text: "#115E59" }, // teal
  { primary: "#7E57C2", soft: "rgba(126, 87, 194, 0.08)", border: "rgba(126, 87, 194, 0.25)", text: "#4527A0" }, // purple
  { primary: "#2E7D32", soft: "rgba(46, 125, 50, 0.08)", border: "rgba(46, 125, 50, 0.25)", text: "#1B5E20" }, // green
  { primary: "#1976D2", soft: "rgba(25, 118, 210, 0.08)", border: "rgba(25, 118, 210, 0.25)", text: "#0D47A1" }, // blue
];

const SPEAKER_PALETTE_DARK: SpeakerColor[] = [
  { primary: "#5C6BC0", soft: "rgba(92, 107, 192, 0.16)", border: "rgba(92, 107, 192, 0.40)", text: "#aab4e8" }, // indigo
  { primary: "#E8620C", soft: "rgba(232, 98, 12, 0.16)", border: "rgba(232, 98, 12, 0.40)", text: "#f0ab7a" }, // orange
  { primary: "#26A69A", soft: "rgba(38, 166, 154, 0.16)", border: "rgba(38, 166, 154, 0.40)", text: "#7fd4c9" }, // teal
  { primary: "#7E57C2", soft: "rgba(126, 87, 194, 0.16)", border: "rgba(126, 87, 194, 0.40)", text: "#c9b3ec" }, // purple
  { primary: "#43A047", soft: "rgba(67, 160, 71, 0.16)", border: "rgba(67, 160, 71, 0.40)", text: "#9fd8a2" }, // green
  { primary: "#1E88E5", soft: "rgba(30, 136, 229, 0.16)", border: "rgba(30, 136, 229, 0.40)", text: "#90c5f7" }, // blue
];

export const SPEAKER_COLOR_PALETTES: Record<ColorMode, SpeakerColor[]> = {
  light: SPEAKER_PALETTE_LIGHT,
  dark: SPEAKER_PALETTE_DARK,
};

export const getSpeakerColorToken = (
  index: number,
  mode: ColorMode
): SpeakerColor => {
  const palette = SPEAKER_COLOR_PALETTES[mode];
  return palette[((index % palette.length) + palette.length) % palette.length];
};
